import { useEffect, useState, useRef } from 'react'

import Image from '../../../../components/Image/Image'

import authProvider from '../../../../utils/auth-provider'
import { fetchClient } from '../../../../utils/fetch'
import { getCssVar } from '../../../../utils/css'
import { createEvent } from '../../../../utils/dom'

export default function DashboardSettingsInputUpload (props = {
    id: null,
    name: null,
    label: null,
    text: null,
    type: null,
    accept: null,
    preview: null,
    previewFallback: null,
    storageBasePath: null,
    storageDirName: null,
    inputHiddenRef: null,
    onChange: null,
    validationMessage: null
}) {
    if (!props.id)
        return null

    const {
        filenamePrefix,
        label,
        text,
        type,
        accept,
        preview,
        previewFallback,
        storageBasePath,
        storageDirName,
        inputHiddenRef,
        onChange,
        validationMessage,
        ...renderProps
    } = props

    let fileAccept = accept

    const fileType = type || 'file'

    if (!fileAccept
            && fileType === 'image')
        fileAccept = '.jpg, .jpeg, .png, .svg, .gif'

    if (!fileAccept
            && fileType === 'pdf')
        fileAccept = '.pdf'

    if (!fileAccept
            && fileType === 'video')
        fileAccept = '.mp4, .webm'

    const inputFile = useRef()

    const inputHidden = useRef()

    const onRefCallback = (ref) => {
        inputHidden.current = ref

        if (inputHiddenRef
                && typeof inputHiddenRef === 'function')
            inputHiddenRef(ref)
    }

    const [file, setFile] = useState()

    const [value, setValue] = useState()

    const [previewPath, setPreviewPath] = useState(preview)

    const getPreviewFilename = () => previewPath
        && previewPath.split('/')
            .reverse()[0]
            .replace(/-\d+(\..{2,}$)/, '$1')
            .replace(`${authProvider.getIdentity().id}-`, '')

    let isCircleAvatar = previewFallback
        && Boolean((typeof previewFallback)
            .match(/(object|string)/))

    if (filenamePrefix === 'company-logo'
            && (file
                || value
                || previewPath))
        isCircleAvatar = false

    useEffect(() => {
        if (previewPath !== preview)
            setPreviewPath(preview)
    }, [preview])

    useEffect(async () => {
        if (!file && value)
            setValue(null)

        const isSizeInvalid = fileType === 'image'
            && ((file || {}).size || 0) > 1048576 * 2

        if (!file
                || isSizeInvalid)
            return

        const identity = authProvider.getIdentity()

        if (!identity.id)
            return setFile(null)

        let ext

        if (fileType === 'image') {
            if (file.type.match(/jpeg/))
                ext = 'jpg'

            if (file.type.match(/svg/))
                ext = 'svg'

            if (file.type.match(/png/))
                ext = 'png'

            if (file.type.match(/gif/))
                ext = 'gif'
        }

        if (fileType === 'pdf')
            ext = 'pdf'

        if (fileType === 'video')
            ext = file.type.match(/webm/)
                ? 'webm'
                : 'mp4'

        ext = ext
            || file.name
                .replace(/^.+(\..+)$/, '$1')
                .replace(/^^\./, '')

        const filename = `${
            filenamePrefix
                ? `${filenamePrefix}-`
                : file.name.replace(/\..{2,}$/, '-')}${(
            new Date()).getTime()}.${
            ext}`

        const basePath = `${(storageBasePath || '').replace(/\/+$/, '')
            || '/storage/o/public'}/${identity.id}`

        const dirName = (storageDirName || '').replace(/\/+$/, '')

        const path = fileType === 'image'
            ? `${basePath}/${dirName
                ? `${dirName}/`
                : ''}${filename}`
            : `${basePath}-${filename}`

        const formData = new window.FormData()

        formData.append('file', file)

        const res = await fetchClient
            .post(path, {
                body: formData
            })
            .catch(err => err)

        if (!res.key)
            return console.warn(res)

        setValue(res.key
            ? path
            : '')
    }, [file])

    const dispatchInputHiddenChange = () => {
        if (!inputHidden.current)
            return

        const event = createEvent('change')

        inputHidden.current.dispatchEvent(event)
    }

    useEffect(() => {
        const onInputHiddenChange = (e) => {
            if (typeof onChange !== 'function')
                return

            onChange(e)
        }

        if (inputHidden.current)
            inputHidden.current
                .addEventListener('change', onInputHiddenChange)

        dispatchInputHiddenChange()

        return () => inputHidden.current
            && inputHidden.current
                .removeEventListener('change', onInputHiddenChange)
    }, [value])

    const onRemove = () => {
        if (inputFile.current
                && inputFile.current.value)
            inputFile.current.value = ''

        setFile(null)
        setPreviewPath(null)
        setValue(null)
    }

    const renderImagePreview = () => (
        <div
            className={`ratio-square font-weight-normal${
                isCircleAvatar
                    ? ' round'
                    : ''}`}
            style={{
                width: 124,
                height: 124,
                fontSize: !(value || previewPath)
                    ? 50
                    : 'inherit',
                letterSpacing: !(value || previewPath)
                    ? 6
                    : 'normal',
                backgroundColor: !(value || previewPath)
                    ? getCssVar('--mystic')
                    : null,
                color: getCssVar('--oxford')
            }}
        >
            <div className="display-flex flex-grow flex-justify-content-center flex-align-items-center">
                {!(value || previewPath)
                    && previewFallback}
                {(value || previewPath)
                    && (
                        <Image
                            src={`${value || previewPath}?w=256`}
                            alt="Upload image preview"
                            textFallback={authProvider.getInitials()}
                            style={{
                                position: 'absolute'
                            }}
                            data-object-fit={isCircleAvatar
                                ? 'cover'
                                : 'contain'}
                            loading="lazy"
                        />)}
            </div>
        </div>
    )

    const renderFilePreview = (
        icon = `icon-${
            fileType === 'pdf'
                    || (preview || '').match(/\.pdf$/)
                ? 'picture-as-pdf'
                : (fileType === 'video'
                    ? 'play-circle'
                    : 'description')}`
    ) => (
        <div
            className="ratio-wide flex-align-items-center flex-justify-content-center"
            style={{
                backgroundColor: previewPath
                    ? getCssVar('--mystic')
                    : null
            }}
        >
            <svg
                className={`${icon} flex-none`}
                width="28"
                height="28"
            >
                <use xlinkHref={`#${icon}`} />
            </svg>
        </div>
    )

    return (
        <div
            className={`form-field dashboard-settings-input-upload row-condensed flex-align-items-center ${
                fileType === 'image'
                    ? 'margin-4-b margin-5-b-lg-up'
                    : 'margin-3-b'}`}
        >
            {(value
                    || fileType !== 'image'
                    || (fileType === 'image'
                        && (previewPath || previewFallback)))
                && (
                    <div className="col-auto pad-3-r-lg-up">
                        <div className="display-flex flex-direction-column">
                            {fileType === 'image'
                                ? renderImagePreview()
                                : renderFilePreview()}
                        </div>
                    </div>)}
            <div className="col">
                <input
                    ref={onRefCallback}
                    type="text"
                    value={value || previewPath || ''}
                    tabIndex="-1"
                    onChange={(e) => {}}
                    {...renderProps}
                />
                {label !== false
                    && (
                        <label
                            htmlFor={`${props.id}-file`}
                            className="font-weight-medium"
                        >
                            {label || `Upload a ${fileType}`}
                        </label>)}
                {text
                    && <p className="body-4 margin-1-b">{text}</p>}
                {fileType !== 'image'
                    && Boolean(previewPath)
                    && (
                        <div className="row-condensed flex-nowrap">
                            <div
                                className="col-auto"
                                style={{
                                    maxWidth: 'calc(100% - 44px)'
                                }}
                            >
                                <p
                                    className="margin-1-b display font-weight-normal"
                                    style={{
                                        color: getCssVar('--navy')
                                    }}
                                >
                                    {getPreviewFilename()}
                                </p>
                            </div>
                            {!props.disabled
                                && (
                                    <div
                                        className="col pad-0-l"
                                        style={{
                                            minWidth: 34
                                        }}
                                    >
                                        <button
                                            className="btn-unstyled"
                                            type="button"
                                            title={`Remove ${getPreviewFilename()}`}
                                            onClick={onRemove}
                                            style={{
                                                minWidth: 24,
                                                minHeight: 24,
                                                marginTop: 1
                                            }}
                                        >
                                            <svg
                                                className="icon-close"
                                                width="24"
                                                height="24"
                                            >
                                                <use xlinkHref="#icon-close" />
                                            </svg>
                                        </button>
                                    </div>)}
                        </div>
                    )}
                <input
                    id={`${props.id}-file`}
                    type="file"
                    className={props.disabled
                        ? 'text-screen-reader'
                        : null}
                    accept={fileAccept}
                    aria-label={label === false
                        && `Upload ${fileType}`}
                    onChange={(e) => setFile(e.target.files[0])}
                    disabled={props.disabled}
                    ref={inputFile}
                />
                {validationMessage
                    && (
                        <p
                            id={`${props.id}-file-invalid`}
                            role="alert"
                        >
                            {validationMessage}
                        </p>)}
                {fileType === 'image'
                    && previewPath
                    && !props.disabled
                    && (
                        <div className="display-flex margin-1-t">
                            <button
                                className="dashboard-settings-input-upload-btn-remove btn-unstyled"
                                type="button"
                                title={`Remove ${getPreviewFilename()}`}
                                onClick={onRemove}
                            >
                                <span className="body-4">Remove {fileType}</span>
                            </button>
                        </div>
                    )}
            </div>
        </div>
    )
}
